import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function TrazerFamilia() {
  return (
    <GuideLayout
      title="Como trazer família para o Japão"
      subtitle="O visto de dependente (家族滞在 / kazoku taizai) explicado em português. Requisitos, documentos, custos, e o que muda na vida quando a família chega."
      slug="trazer-familia-para-o-japao"
      description="Guia completo para trazer esposa, marido e filhos para o Japão. Visto de dependente kazoku taizai, documentos, prazos, custos."
      updated="Maio de 2026"
      readTime="11 min"
    >
      <P>
        Você veio sozinho. Pegou o ritmo, está conseguindo guardar dinheiro,
        e agora quer trazer a esposa, marido, ou filhos. É um dos momentos
        mais importantes da vida de quem mora no Japão — e um dos mais mal
        explicados. Este guia mostra o que precisa, quanto custa, e os
        cuidados práticos.
      </P>

      <H2 id="o-que-eh">O que é o visto de dependente</H2>
      <P>
        Para trazer família depende de quem é o titular e qual o visto:
      </P>
      <UL>
        <li>
          <Strong>Trabalhador com visto temporário (engineer, specialist, etc.):</Strong>{" "}
          a família vem pelo visto <Em>kazoku taizai</Em> (家族滞在). Permite
          morar com você, ir à escola, mas trabalhar só 28h/semana.
        </li>
        <li>
          <Strong>Permanente (永住者):</Strong> sua família vem por visto de{" "}
          <Em>long-term resident</Em> (定住者) ou <Em>permanent resident
          spouse</Em> (永住者の配偶者) com direito a trabalhar livremente.
        </li>
        <li>
          <Strong>Long-term resident (定住者) — caso de muitos descendentes japoneses:</Strong>{" "}
          família vem por <Em>long-term resident</Em> sem restrição de
          trabalho.
        </li>
      </UL>
      <Callout>
        <Strong>Diferença crítica:</Strong> com kazoku taizai (dependente
        comum), seu cônjuge pode trabalhar apenas 28 horas por semana. Com
        long-term resident, pode trabalhar sem limite. Faz diferença enorme
        na renda familiar.
      </Callout>

      <H2 id="quem-pode-trazer">Quem você pode trazer</H2>
      <UL>
        <li>
          <Strong>Cônjuge legal</Strong> (esposa ou marido, com certidão de
          casamento)
        </li>
        <li>
          <Strong>Filhos biológicos ou adotados</Strong> menores de idade ou
          dependentes
        </li>
      </UL>
      <P>
        Não cobre:
      </P>
      <UL>
        <li>Companheiro(a) em união estável sem casamento formal</li>
        <li>Pais idosos (existe categoria especial em casos raros)</li>
        <li>Irmãos, primos, sobrinhos</li>
        <li>Filhos adultos independentes</li>
      </UL>

      <H2 id="requisitos-titular">Requisitos para o titular (você)</H2>
      <P>
        A imigração quer ter certeza de que você pode sustentar a família.
        Eles checam:
      </P>
      <UL>
        <li>
          <Strong>Status válido:</Strong> seu zairyu card precisa estar em
          vigor e em status que permite dependentes
        </li>
        <li>
          <Strong>Tempo de visto restante:</Strong> imigração olha quanto
          tempo ainda tem no seu visto — pouco tempo restante reduz chance de aprovação
        </li>
        <li>
          <Strong>Renda mínima:</Strong> não há um número fixo na lei, mas a
          referência prática é ¥200.000+/mês líquido para um dependente,
          ¥250.000+ para dois, ¥300.000+ para família de 3 ou mais
        </li>
        <li>
          <Strong>Impostos quitados:</Strong> juushinkoku e shakai hoken em
          dia. Qualquer atraso pode rejeitar
        </li>
        <li>
          <Strong>Moradia adequada:</Strong> contrato de aluguel no seu nome,
          espaço razoável (não dá pra trazer família para um shoebox de 12m²)
        </li>
      </UL>
      <Callout kind="warn">
        Se você ganha o mínimo possível (¥180.000 líquido) ou tem dívida de
        imposto, a chance de aprovação cai muito. Trabalhe os números antes
        de aplicar. Use a{" "}
        <a href="/calculadoras/salario" className="text-accent underline">
          calculadora de salário
        </a>{" "}
        pra ver onde está.
      </Callout>

      <H2 id="documentos-titular">Documentos do titular (você no Japão)</H2>
      <OL>
        <li><Strong>Cópia do zairyu card</Strong> (frente e verso)</li>
        <li><Strong>Cópia do passaporte</Strong> (página principal)</li>
        <li>
          <Strong>Certificado de trabalho</Strong> (在職証明書) emitido pelo
          RH da empresa, em japonês
        </li>
        <li>
          <Strong>Comprovante de renda</Strong> (源泉徴収票) do último ano
          fiscal completo + último contracheque
        </li>
        <li>
          <Strong>Comprovante de imposto residencial</Strong> (住民税の納税
          証明書) — peça na prefeitura, mostra que você pagou
        </li>
        <li>
          <Strong>Juuminhyou</Strong> (住民票) atual com todos os moradores
        </li>
        <li>
          <Strong>Saldo bancário</Strong> (opcional mas ajuda) — extrato dos
          últimos 6 meses mostrando reserva
        </li>
        <li>
          <Strong>Contrato de aluguel</Strong> do seu apartamento
        </li>
        <li>
          <Strong>Carta de explicação</Strong> em japonês: por que quer
          trazer a família, plano de vida no Japão, como vai sustentá-los
          (modelos online em sites de gyouseishoshi)
        </li>
      </OL>

      <H2 id="documentos-familia">Documentos da família (no Brasil)</H2>
      <P>
        Tudo precisa ser traduzido para o japonês por tradutor reconhecido
        (não JAF, esses são pra carteira de motorista — aqui precisa
        tradutor juramentado registrado no Japão ou na embaixada). Documentos:
      </P>
      <OL>
        <li><Strong>Passaporte</Strong> válido por pelo menos 1 ano</li>
        <li>
          <Strong>Certidão de casamento</Strong> (para cônjuge) — original
          recente, emitida nos últimos 6 meses
        </li>
        <li>
          <Strong>Certidão de nascimento</Strong> (para filhos) — original
          recente
        </li>
        <li>
          <Strong>Histórico escolar</Strong> (para filhos em idade escolar) —
          ajuda na matrícula no Japão
        </li>
        <li>
          <Strong>Antecedentes criminais</Strong> (atestado de boa conduta) —
          obrigatório para adultos
        </li>
        <li>
          <Strong>Cartão de vacinação</Strong> (para crianças) — Japão exige
          vacinas atualizadas
        </li>
      </OL>

      <H2 id="processo">O processo: passo a passo</H2>
      <OL>
        <li>
          <Strong>Junte documentos do seu lado no Japão</Strong> (1-2 semanas
          pra conseguir tudo na empresa e prefeitura)
        </li>
        <li>
          <Strong>Família junta documentos no Brasil</Strong> em paralelo
          (2-3 semanas pra cartório, traduções, antecedentes)
        </li>
        <li>
          <Strong>Envie documentos do Brasil para o Japão</Strong> via DHL ou
          Fedex (¥10.000-20.000)
        </li>
        <li>
          <Strong>Você submete &ldquo;Certificate of Eligibility&rdquo; (COE)</Strong>{" "}
          na imigração japonesa em nome de cada familiar — taxa zero pra COE
        </li>
        <li>
          <Strong>Aguarde 1-3 meses</Strong> a imigração processar
        </li>
        <li>
          <Strong>COE aprovada chega na sua mão</Strong> — você envia
          original para o Brasil
        </li>
        <li>
          <Strong>Família vai ao consulado japonês no Brasil</Strong>{" "}
          (São Paulo, Brasília, Curitiba, Recife, Manaus, Belém, Porto Alegre,
          Rio) com a COE e o passaporte
        </li>
        <li>
          <Strong>Consulado emite o visto</Strong> em 5-7 dias úteis (¥3.000
          equivalente por pessoa)
        </li>
        <li>
          <Strong>Família compra passagem e vem para o Japão</Strong> dentro
          de 3 meses da emissão do visto
        </li>
        <li>
          <Strong>Na chegada</Strong>, recebem zairyu card no aeroporto
          (aeroportos principais) ou pedem pra ser entregue no endereço
        </li>
      </OL>

      <Callout>
        Tempo total típico: <Strong>3 a 5 meses</Strong> do início ao
        embarque. Não conte com menos. Use esse tempo para preparar moradia,
        escola, ginecologista (em caso de gestante), etc.
      </Callout>

      <H2 id="custos">Custos reais</H2>
      <UL>
        <li><Strong>Documentos no Brasil</Strong> (certidões, antecedentes, traduções): R$ 500–1.500 por adulto, R$ 200–600 por criança</li>
        <li><Strong>DHL/Fedex documentos:</Strong> R$ 200–400 ou ¥10.000–20.000</li>
        <li><Strong>Taxa do COE no Japão:</Strong> grátis</li>
        <li><Strong>Visto no consulado:</Strong> ~R$ 100 por pessoa</li>
        <li><Strong>Passagem aérea:</Strong> R$ 6.000–12.000 por adulto (depende da época)</li>
        <li><Strong>Gyouseishoshi (opcional):</Strong> ¥150.000–400.000 (¥4.000-10.000 por familiar)</li>
        <li><Strong>Mudança de moradia</Strong> (se sair de quarto compartilhado): ¥200.000–500.000 (sikkikin + reikin + 1 mês adiantado)</li>
        <li><Strong>Móveis básicos:</Strong> ¥80.000–200.000</li>
        <li><Strong>Roupas de inverno se chegar antes do calor:</Strong> ¥30.000–80.000 por pessoa</li>
      </UL>
      <P>
        <Strong>Custo realista total:</Strong> ¥800.000 a ¥1.500.000 (R$ 30.000 a R$ 55.000)
        considerando família de 2-3 pessoas. Tenha essa reserva antes de
        começar — não conte com vir junto.
      </P>

      <H2 id="apos-chegada">Após a chegada: o que fazer nos primeiros 14 dias</H2>
      <OL>
        <li>
          <Strong>Registrar endereço na prefeitura</Strong> (14 dias) — vai
          gerar juuminhyou da família
        </li>
        <li>
          <Strong>Inscrever no kokumin kenko hoken</Strong> (se dependente
          não tem shakai hoken pela sua empresa)
        </li>
        <li>
          <Strong>Inscrever no kokumin nenkin</Strong> (idem)
        </li>
        <li>
          <Strong>Matricular filhos na escola</Strong> — gratuita até o
          chuugakkou (jr high). Veja sobre escola pra brasileiros no Japão
        </li>
        <li>
          <Strong>Pegar My Number</Strong> da família — chega por correio em
          2-3 semanas
        </li>
        <li>
          <Strong>Abrir conta bancária</Strong> pros adultos (yucho aceita
          recém-chegados)
        </li>
        <li>
          <Strong>Tirar gaimen kirikae</Strong> se família tem CNH (veja{" "}
          <a href="/guias/gaimen-kirikae" className="text-accent underline">
            guia específico
          </a>
          )
        </li>
      </OL>

      <H2 id="kazoku-taizai-vs-other">Kazoku taizai vs outros visto: trabalho do cônjuge</H2>
      <P>
        Esta é a diferença mais importante na qualidade de vida da família:
      </P>
      <UL>
        <li>
          <Strong>Kazoku taizai (visto temporário):</Strong> cônjuge pode
          trabalhar até <Strong>28 horas/semana</Strong>. Precisa pedir
          autorização (資格外活動 / shikakugai katsudou). Renda extra é
          bem-vinda mas limitada.
        </li>
        <li>
          <Strong>Long-term ou Permanent Resident spouse:</Strong> trabalho
          ilimitado, qualquer profissão, qualquer fábrica. Renda familiar
          pode dobrar.
        </li>
      </UL>
      <Callout>
        <Strong>Implicação prática:</Strong> se você está pensando em pedir
        permanente, considere fazer isso ANTES de trazer a família. Cônjuge
        de permanente pode trabalhar livremente desde o dia da chegada.
        Diferença pode ser de ¥200.000/mês a mais de renda familiar.
      </Callout>

      <H2 id="dificuldades">As partes difíceis que ninguém fala</H2>
      <UL>
        <li>
          <Strong>Adaptação dos filhos:</Strong> crianças pequenas se adaptam
          rápido. Adolescentes (13-17) têm enorme dificuldade — escola,
          idioma, identidade. Considere muito bem antes de trazer adolescente.
        </li>
        <li>
          <Strong>Idioma do cônjuge:</Strong> se chega sem falar japonês, vai
          depender de você pra tudo no início. Isso desgasta o casamento se
          não for planejado. Insira aulas de japonês no orçamento (¥10.000–30.000/mês).
        </li>
        <li>
          <Strong>Solidão da esposa em casa:</Strong> esposa que era trabalhadora
          no Brasil e vira &ldquo;dona de casa&rdquo; no Japão sem rede de amigos
          frequentemente desenvolve depressão. Conecte-a a grupos de brasileiros
          antes da chegada.
        </li>
        <li>
          <Strong>Custo de mudança maior que esperado:</Strong> a estimativa
          inicial dobra. Sempre tenha reserva de 50% além do orçamento.
        </li>
        <li>
          <Strong>Diferença de expectativa:</Strong> você lembra da fábrica.
          Eles imaginam Tóquio dos filmes. A vida em Hamamatsu ou Toyota não
          é o Japão dos turismos. Mande fotos reais, vídeos do cotidiano,
          conversa franca antes da decisão.
        </li>
      </UL>

      <H2 id="conclusao">Conclusão</H2>
      <P>
        Trazer a família para o Japão é uma das melhores decisões que muito
        dekassegui já fez. É também uma das mais subestimadas em custo,
        tempo e ajuste emocional.
      </P>
      <P>
        Os números honestos:
      </P>
      <UL>
        <li>3-5 meses de processo</li>
        <li>¥1 milhão de gasto inicial</li>
        <li>Renda mínima familiar de ¥250.000+ líquido</li>
        <li>Disposição para reconstruir vida em equipe, num país que não fala sua língua</li>
      </UL>
      <P>
        Se você tem as 4 coisas acima alinhadas, vale a pena. Se não tem,
        espere — trazer família sem preparo financeiro emocional é
        a maneira mais rápida de transformar oportunidade em crise.
      </P>
    </GuideLayout>
  );
}
